
import type { Json } from '@/integrations/supabase/types';
import type { AdditionalContact, PayrollSchedule, HealthPlan, CorporateStructure, Application } from '@/types/database.types';

export function processAdditionalContacts(contacts: Json): AdditionalContact[] {
  if (!contacts) return [];
  if (typeof contacts === 'string') {
    try {
      return JSON.parse(contacts);
    } catch (e) {
      console.error('Error parsing additional_contacts', e);
      return [];
    }
  }
  if (Array.isArray(contacts)) {
    // Cast through unknown first, then filter with proper type checking
    return (contacts as unknown as any[]).filter((contact: any): contact is AdditionalContact => {
      return contact && typeof contact === 'object' && typeof contact.first_name === 'string';
    });
  }
  return [];
}

export function processPayrollSchedules(schedules: Json[]): PayrollSchedule[] {
  if (!schedules || !Array.isArray(schedules)) return [];
  return schedules.map(schedule => {
    if (typeof schedule === 'string') {
      try {
        return JSON.parse(schedule);
      } catch (e) {
        console.error('Error parsing payroll schedule', e);
        return {};
      }
    }
    // Cast through unknown and check for required properties
    if (schedule && typeof schedule === 'object') {
      const scheduleObj = schedule as unknown as any;
      if (scheduleObj.schedule_type) {
        return scheduleObj as PayrollSchedule;
      }
    }
    return {} as PayrollSchedule;
  }).filter(schedule => schedule && typeof schedule === 'object' && (schedule as any).schedule_type);
}

export function processHealthPlans(plans: Json): HealthPlan[] {
  if (!plans) return [];
  if (typeof plans === 'string') {
    try {
      return JSON.parse(plans);
    } catch (e) {
      console.error('Error parsing health_plans', e);
      return [];
    }
  }
  if (Array.isArray(plans)) {
    // Cast through unknown first, then filter with proper type checking
    return (plans as unknown as any[]).filter((plan: any): plan is HealthPlan => {
      return plan && typeof plan === 'object' && typeof plan.plan_name === 'string';
    });
  }
  return [];
}

export function ensureStringArray(value: any): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [value];
    } catch (e) {
      return [value];
    }
  }
  return [];
}

export function ensureDocumentArray(value: any): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    // For document fields, if it's a single string, wrap it in an array
    return [value];
  }
  return [];
}

export function normalizeCorporateStructure(value: any): CorporateStructure | null {
  if (!value) return null;
  return value as CorporateStructure;
}

/**
 * Comprehensive transformation utility that converts raw database application data
 * to properly typed Application interface, handling all field type conversions
 */
export function transformApplicationData(rawData: any): Application {
  // Process all document fields that should be arrays but might be strings
  const documentFields = [
    'employee_census_file',
    'dependent_census_file',
    'workers_comp_declaration_file',
    'workers_comp_loss_runs_file',
    'health_insurance_renewal_file',
    'sbc_file',
    'tax_exempt_status_file',
    'other_documents'
  ];

  // Process all array fields that might be stored as JSON strings
  const arrayFields = [
    'desired_medical_carriers',
    'desired_ancillary_carriers',
    'states_of_operation',
    'states_with_employees',
    'top_3_peo_needs',
    'preferred_peos',
    'excluded_peos'
  ];

  const transformed: any = { ...rawData };

  // Transform document fields to arrays
  documentFields.forEach(field => {
    transformed[field] = ensureDocumentArray(rawData[field]);
  });

  // Transform array fields
  arrayFields.forEach(field => {
    transformed[field] = ensureStringArray(rawData[field]);
  });

  // Process complex JSON fields
  transformed.health_plans = processHealthPlans(rawData.health_plans);
  transformed.additional_contacts = processAdditionalContacts(rawData.additional_contacts);
  transformed.additional_schedules = processPayrollSchedules(rawData.additional_schedules as Json[]);
  
  // Process corporate structure
  transformed.corporate_structure = normalizeCorporateStructure(rawData.corporate_structure);

  return transformed as Application;
}
